import React, { useState, useEffect } from 'react'
import { BaseModal } from '../../../../components/Modal/BaseModal'
import type { BaseModalProps } from '../../../../components/Modal/BaseModal'
import { Button } from '../../../../components/Button'
import { Textarea } from '../../../../components/Textarea'
import { Checkbox } from '../../../../components/Checkbox'
import { StarRating } from '../../../../components/StarRating/starrating'
import { HiCheckCircle } from 'react-icons/hi2'

export interface SolveDemandModalProps extends Omit<BaseModalProps, 'children'> {
  onConfirm: (data: { comentario: string; solucionarTudo: boolean; nota: number }) => void
  onCancel?: () => void
  loading?: boolean
  disabled?: boolean
}

export const SolveDemandModal: React.FC<SolveDemandModalProps> = ({
  title = 'Solucionar Demanda',
  onConfirm,
  onCancel,
  loading = false,
  disabled = false,
  isOpen,
  onClose,
  ...props
}) => {
  const [comentario, setComentario] = useState('')
  const [solucionarTudo, setSolucionarTudo] = useState(false)
  const [nota, setNota] = useState(5) // Nota padrão: 5 estrelas
  const [isDefaultText, setIsDefaultText] = useState(true)

  // Texto padrão para solução
  const defaultText =
    '<p><strong>Solução implementada:</strong></p><p>• A demanda foi analisada e solucionada conforme especificado</p><p>• Todas as etapas necessárias foram executadas</p><p>• Resultado obtido com sucesso</p><p><br></p><p><em>Comentários adicionais:</em></p>'

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setComentario(defaultText)
      setSolucionarTudo(false)
      setNota(5) // Reset para 5 estrelas
      setIsDefaultText(true)
    }
  }, [isOpen])

  const handleCancel = () => {
    setComentario('')
    setSolucionarTudo(false)
    setNota(5)
    setIsDefaultText(true)
    if (onCancel) {
      onCancel()
    }
    onClose()
  }

  const handleConfirm = () => {
    if (!comentario.trim()) {
      return
    }

    onConfirm({
      comentario: comentario.trim(),
      solucionarTudo,
      nota,
    })
  }

  const handleTextChange = (value: string) => {
    setComentario(value)
    // Se o usuário começou a editar, não é mais texto padrão
    if (isDefaultText && value !== defaultText) {
      setIsDefaultText(false)
    }
  }

  const handleTextFocus = () => {
    // Se ainda é texto padrão, limpar para permitir edição
    if (isDefaultText) {
      setComentario('')
      setIsDefaultText(false)
    }
  }

  const isValid = comentario.trim().length > 0

  const getIcon = () => {
    return <HiCheckCircle className='w-8 h-8 text-green-500' />
  }

  const getDescription = () => {
    return 'Registre como a demanda foi solucionada. Use o editor de texto rico para formatação adequada.'
  }

  const footerActions = (
    <div className='flex items-center justify-end space-x-3 p-4'>
      <Button
        variant='outline'
        size='sm'
        onClick={handleCancel}
        disabled={loading}
        className='min-w-20'
      >
        Cancelar
      </Button>

      <Button
        variant='primary'
        size='sm'
        onClick={handleConfirm}
        disabled={disabled || !isValid}
        isLoading={loading}
        className={`min-w-32 ${solucionarTudo ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}`}
      >
        {solucionarTudo ? 'Solucionar por Todos' : 'Solucionar'}
      </Button>
    </div>
  )

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleCancel}
      title={title}
      description={getDescription()}
      icon={getIcon()}
      useStandardLayout={true}
      size='xl'
      closeOnOverlayClick={!loading}
      closeOnEscape={!loading}
      resizable={true}
      retractable={true}
      stickyFooter={true}
      footerContent={footerActions}
      {...props}
    >
      <div className='p-4'>
        {/* Message */}
        <div className='text-center mb-4'>
          <p className='text-sm text-gray-900 dark:text-gray-100 leading-relaxed'>
            Descreva como a demanda foi solucionada. Este comentário será registrado no histórico.
          </p>
        </div>

        {/* Seção de Avaliação da Qualidade */}
        <div className=''>
          {/* <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 text-center'>
            Avalie a qualidade da solução implementada
          </label> */}
          <div className='flex items-center justify-center py-2'>
            <StarRating
              totalStars={5}
              defaultValue={nota}
              size='lg'
              disabled={false}
              onRate={setNota}
              className='opacity-100'
            />
          </div>
          {/* <p className='text-xs text-center text-gray-600 dark:text-gray-400 mt-2'>
            {nota === 5 && '⭐ Excelente - Qualidade excepcional'}
            {nota === 4 && '⭐ Muito Bom - Acima das expectativas'}
            {nota === 3 && '⭐ Bom - Atende as expectativas'}
            {nota === 2 && '⭐ Regular - Necessita melhorias'}
            {nota === 1 && '⭐ Insuficiente - Abaixo do esperado'}
          </p> */}
        </div>
        <p className='text-xs text-center text-green-700 dark:text-green-300 mt-2'>
          Certifique-se de que a solução atende aos mais altos padrões de qualidade
        </p>

        {/* Rich Text Editor para comentário */}
        <div className='mb-4'>
          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
            Comentário sobre a solução <span className='text-red-500'>*</span>
          </label>
          <Textarea
            variant='rich'
            value={comentario}
            onChange={handleTextChange}
            onFocus={handleTextFocus}
            placeholder='Descreva detalhadamente como a demanda foi solucionada, quais ações foram tomadas, resultados obtidos, etc.'
            disabled={loading || disabled}
            rows={8}
            className='w-full'
            label=''
            description=''
          />
          <div className='text-xs text-gray-500 dark:text-gray-400 mt-1'>
            {isDefaultText
              ? 'Texto padrão - clique para personalizar'
              : comentario.length > 0
                ? `${comentario.length} caracteres`
                : 'Campo obrigatório'}
          </div>
        </div>

        {/* Checkbox para solucionar tudo */}
        <div className='mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800'>
          <div className='space-y-2'>
            <Checkbox
              id='solucionarTudo'
              checked={solucionarTudo}
              onChange={e => setSolucionarTudo(e.target.checked)}
              disabled={loading || disabled}
              label='Solucionar por todos os responsáveis'
            />
            <p className='text-xs text-gray-600 dark:text-gray-400 ml-6'>
              Marque esta opção se você está solucionando a demanda em nome de todos os responsáveis
              atribuídos.
            </p>
          </div>
        </div>

        {/* Mensagem informativa baseada na seleção */}
        <div className='mb-6 p-3 rounded-lg bg-gray-50 dark:bg-gray-800'>
          <div className='text-xs text-gray-600 dark:text-gray-400'>
            {solucionarTudo ? (
              <>
                <strong>Atenção:</strong> A demanda será marcada como solucionada por TODOS os
                responsáveis atribuídos. Esta ação só deve ser usada quando você tem autorização
                para representar toda a equipe.
              </>
            ) : (
              <>
                A demanda será marcada como solucionada apenas por você. Outros responsáveis ainda
                precisarão marcar suas próprias soluções (se aplicável).
              </>
            )}
          </div>
        </div>
      </div>
    </BaseModal>
  )
}
